import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm, type SubmitHandler } from 'react-hook-form';
import type { PageExperience } from '@/features/experiences/types';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from '@/ui';
import {
  schoolReportFormSchema,
  type SchoolReportFormValues,
} from './schoolReportForm.schema';
import { useSubmitSchoolFeedbackForm } from './useSubmitSchoolFeedbackForm';

type Props = {
  experience: PageExperience;
  isOpen: boolean;
  onClose: () => void;
};

export const SchoolReportModal = ({ experience, isOpen, onClose }: Props) => {
  const { submitForm, isPending } = useSubmitSchoolFeedbackForm();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { email: '', notes: '', pdfUrl: '' },
    resolver: standardSchemaResolver(schoolReportFormSchema),
  });

  const onSubmit: SubmitHandler<SchoolReportFormValues> = (values) => {
    submitForm(
      {
        formId: 'school-report',
        experienceId: experience.id,
        email: values.email,
        notes: values.notes,
        pdfUrl: values.pdfUrl || undefined,
      },
      {
        onSuccess: () => {
          reset();
          toast.notify(`Thanks for the heads up — our team will take a look.`);
          onClose();
        },
        onError: () => {
          toast.error('Error submitting, please try again');
        },
      }
    );
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      size={{ base: 'full', md: 'lg' }}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent px={{ base: '0', sm: '4', md: '8' }} py="6">
        <ModalCloseButton />
        <ModalBody flexDir="column">
          <Heading
            as="h2"
            fontSize={{ base: '2xl', sm: '2xl' }}
            fontWeight="bold"
            mb="6"
          >
            Suggest an edit
          </Heading>
          <Flex
            as="form"
            direction="column"
            gap="5"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormControl isInvalid={Boolean(errors.email)}>
              <Text fontWeight="500" mb="2">
                Your email
              </Text>
              <Input
                placeholder="you@example.com"
                type="email"
                {...register('email')}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.notes)}>
              <Text fontWeight="500" mb="2">
                What&apos;s the issue?
              </Text>
              <Textarea
                placeholder="e.g., 'The date is wrong — Labor Day is actually Sep 7 this year, not Sep 1'"
                rows={4}
                {...register('notes')}
              />
              <FormErrorMessage>{errors.notes?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.pdfUrl)}>
              <Text fontWeight="500" mb="2">
                If there&apos;s an updated PDF we&apos;d appreciate you sharing
                it here.
              </Text>
              <Input
                placeholder="https://…"
                type="url"
                {...register('pdfUrl')}
              />
              <FormErrorMessage>{errors.pdfUrl?.message}</FormErrorMessage>
            </FormControl>

            <Button isLoading={isPending} type="submit" w="100%">
              Submit
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
